/**
 * THE ONE PLACE A REPEATING TIME IS PUT INTO WORDS.
 *
 * EIFO report #198 (issue #766), part 4 of the approved plan — the visible step:
 * "the shared wording starts speaking for the next lesson. Every screen changes
 * in this one release, so none is right while its neighbour is wrong."
 *
 * 🟢 THE SWITCH IS ON. Part 3 routed all 22 viewer screens through this function
 * while it still reproduced the frozen summer wording character for character;
 * part 4 changed what it answers, and nothing else. That is why the reporter's
 * "all screens should change together" held: the release that changes every
 * screen is a change to `repeatingTime` below, not a sweep.
 *
 * ── What it now answers, and what it still cannot
 *
 * A repeating line speaks for ONE REAL LESSON (part 1's `pickSpeakingLesson`),
 * and the lesson's own stored instant is projected into the reader's zone with
 * `projectInstantRange`, which takes no reference date and so cannot drift. A
 * line with no class behind it has nothing stored to read, so its occurrence is
 * COMPOSED in the clock the programme keeps (`pickSpeakingOccurrence`).
 *
 * 🔴 WITH NEITHER, THE FROZEN WORDING IS STILL WHAT COMES OUT, and deliberately:
 * a class whose sessions are not materialized yet, every session cancelled, or a
 * pattern whose weekday never falls inside the term are real states, and the
 * plan's answer for them is today's text rather than an invented hour. The
 * frozen path below is therefore kept, not deleted — see `fallback` on the
 * result, which says which of the two a caller got.
 *
 * `repeating-time-label.test.ts` asserts the flip: the two expectations part 3
 * pinned on purpose (one answer all year; the Beijing family told 7 AM for a
 * lesson that has moved to 8) are inverted there, and the frozen path is still
 * pinned for the no-lesson case.
 *
 * ── What a caller has to decide, and why the type makes it decide it
 *
 * `frame` is the hazard `slot-label.ts`'s header documents at length: three
 * sources store `{day_of_week, start_time, end_time}` in three different frames
 * and are indistinguishable at the type level, so applying the wrong conversion
 * produces a plausible, silently wrong hour. There is no default here for the
 * same reason there is no one-function-with-a-flag there — the caller knows
 * which table the triple came from, and nothing downstream can work it out.
 *
 * ── Returns raw AND formatted, because the screens need both
 *
 * Several callers bucket rows on the raw 24-hour value and render the formatted
 * one (the catalog calendar floors `rawStartTime` to :00/:30 for its grid; the
 * heatmap keys its cells on it). Handing back only a label would have pushed
 * those screens back onto a frozen helper for the half they could not get here,
 * which is exactly the drift this module exists to end. ⚠️ Never bucket on the
 * formatted strings — "4 PM" and "4:30 PM" do not sort.
 */
import {
  pickSpeakingOccurrence,
  type OccurrenceClock,
  type SpeakingLesson,
} from '@/lib/scheduling/speaking-lesson';
import {
  localTimeToUtc,
  projectInstantRange,
  projectLocalTimeRange,
  utcTimeToLocal,
} from '@/lib/timezone-utils';

/** Which frame the weekly triple is stored in. See `slot-label.ts`'s table. */
export type RepeatingTimeFrame =
  /**
   * COURSE/anchor-zone wall-clock — `catalog_requests.acceptable_patterns`, and
   * the offerings the catalog generates. Needs `anchorTz` and converts twice.
   */
  | 'anchor-tz'
  /**
   * Already UTC — `class_sections.{day_of_week,start_time,end_time}` and the
   * `availability_slots` TABLE. One hop to the reader.
   */
  | 'utc';

/**
 * `SpeakingWindow`'s shape, re-declared here rather than imported.
 *
 * The server's copy lives in `speaking-lesson.resolver.ts`, which pulls in
 * `drizzle-orm`; these callers are client components. Structurally identical, so
 * a payload's window assigns to it without a cast, and `OccurrenceClock` is
 * shared from part 1's module so neither can drift to a bare string.
 */
export type RepeatingTimeWindow = {
  clock: OccurrenceClock;
  /** `YYYY-MM-DD`, already in `clock`. */
  windowStart: string;
  windowEnd: string;
  /**
   * 🔴 THE REQUEST'S INSTANT, STAMPED BY THE SERVER — added by part 4.
   *
   * "Which occurrence comes next" needs a `now`, and `repeatingTime` is pure, so
   * `now` has to arrive with the window rather than be read off the clock here.
   * It rides the window — which only the pattern-only lines carry, and which the
   * server already resolves for exactly those lines — so part 4 needed no second
   * sweep through the screens to thread one more argument in.
   *
   * ⚠️ It must come from the SERVER, not `new Date()` in a component. A value
   * sampled during render differs between the server pass and hydration, and the
   * occurrence it picks is rendered text: two different hours either side of a
   * lesson boundary is a hydration mismatch. One server-stamped instant renders
   * identically in both passes.
   *
   * Staleness is harmless: the occurrence only changes when a lesson ends, and
   * all the reader needs is a date inside the right DST period.
   */
  now: Date;
};

export type RepeatingTimeSlot = {
  dayOfWeek: number;
  /** 24-hour "HH:MM" in the frame named by `frame`. */
  startTime: string;
  endTime: string;
};

export type RepeatingTimeInput = {
  slot: RepeatingTimeSlot;
  frame: RepeatingTimeFrame;
  /** Required when `frame` is `'anchor-tz'`; ignored otherwise. */
  anchorTz?: string | null;
  /**
   * The zone to read the time in. Usually the viewer's own, but three admin
   * analytics screens deliberately pass one fixed zone so every admin discusses
   * one agreed wall clock — passing it in keeps that their decision.
   */
  readerTz: string;
  /**
   * 🟢 THE ANSWER, WHEN THERE IS A CLASS TO POINT AT — read since part 4.
   *
   * The lesson this line speaks for (`pickSpeakingLesson`, part 1), carried from
   * the payloads part 2 added. Its own stored instant is projected, so the line
   * cannot disagree with the dashboard or attendance list beside it, and it is
   * right whichever clock report #200 ends up holding the class to.
   *
   * `null` is a real state, not a failure — a class whose sessions are not
   * materialized yet, or every session cancelled. The frozen wording is kept for
   * those, rather than an invented hour.
   */
  speaking?: SpeakingLesson | null;
  /**
   * 🟢 THE ANSWER FOR A LINE WITH NO CLASS — also read since part 4.
   *
   * The programme's clock, the term's run and the server's `now`, from
   * `SpeakingWindow` on the payloads part 2 added. A pattern a parent said they
   * are free for, or a slot being considered while classes are still being
   * formed, has nothing stored to look up, so `pickSpeakingOccurrence` composes
   * "the next time that weekday comes round inside the term" instead.
   *
   * ⚠️ `speaking` WINS when both are present. A line with a real lesson behind it
   * reads that lesson; the window is the answer only where there is none. Several
   * screens show both kinds of line and pass both values for every row.
   *
   * Never `courses.timezone`: report #200 established a course reaches a
   * programme through a many-to-many join and so cannot name one clock. For an
   * `anchor-tz` slot this means the programme's clock is used in preference to
   * `anchorTz` — see `occurrenceFor`.
   */
  window?: RepeatingTimeWindow | null;
  /**
   * 🔴 A REFERENCE DATE FOR THE FROZEN PATH ONLY, and the one place part 4
   * deliberately does NOT switch over. Read nowhere else.
   *
   * Three rows name their own calendar day and may have no stored instant for it:
   * a substitute absence, a substitute session, and an admin coverage alert, all
   * of which read `projectInstantRange` off `section_instances` when the day IS
   * materialized (reports #195–#197) and reach here only when it is not.
   *
   * ⚠️ WHY THEY MUST NOT TAKE `speaking`. Their section's next lesson is a
   * DIFFERENT week from the date printed beside the hour. Projecting it would put
   * one week's hour next to another week's date — a new way to be wrong, worse
   * than the one this report is about, because the two halves of the same line
   * would contradict each other. So those three pass this instead, and keep the
   * frozen path with the right DST period sampled for the reader's leg.
   *
   * It stays a HALF-measure and is labelled as one: it re-samples the reader's leg
   * alone, so a Beijing reader whose drift lives in the anchor leg still reads the
   * summer hour on such a row. The real fix for them is a materialized instance
   * for the day, which is not this report's to build. Renamed from part 3's
   * `legacyRefDate` because it is no longer a shim awaiting removal — it is the
   * documented answer for a row with a date and nothing stored under it.
   */
  fallbackRefDate?: Date;
};

/**
 * Which route produced the answer. Rendered by nothing — it exists so a test, and
 * part 5's check, can tell "spoke for a real lesson" from "no lesson to speak
 * for, so today's wording" without re-deriving the decision.
 */
export type RepeatingTimeBasis = 'lesson' | 'occurrence' | 'frozen';

export type RepeatingTime = {
  /** Weekday in `readerTz`. An instant crossing midnight lands on another day. */
  dayOfWeek: number;
  /** 24-hour "HH:MM" in `readerTz` — for bucketing and sorting, never display. */
  rawStartTime: string;
  rawEndTime: string;
  /** 12-hour, e.g. `"4 PM"` / `"4:55 PM"`. */
  startTime: string;
  endTime: string;
  /** The reader zone's abbreviation, e.g. `"ET"`. */
  tz: string;
  /** `"4 PM–4:55 PM ET"` — an en dash, as `projectLocalTimeRange` builds it. */
  label: string;
  /** Which route answered. See `RepeatingTimeBasis`. */
  basis: RepeatingTimeBasis;
};

/**
 * Put a repeating weekly time into words for one reader.
 *
 * PURE — no hooks, no `Date.now()`, safe inside `.map()` and on either side of
 * the network.
 */
export function repeatingTime(input: RepeatingTimeInput): RepeatingTime {
  const { slot, frame, anchorTz, readerTz, fallbackRefDate } = input;

  // ── 🟢 THE SWITCH. Everything above this line is plumbing part 3 laid down.
  //
  // A real lesson first, then a composed occurrence, then — only when there is
  // neither — the frozen wording below. One `if`, which is what part 3's 22-file
  // sweep bought: this is the whole of the change every screen sees.
  const speaking = input.speaking ?? occurrenceFor(input);
  if (speaking) {
    const shown = projectInstantRange(speaking.startUtc, speaking.endUtc, readerTz);
    return {
      dayOfWeek: shown.dayOfWeek,
      rawStartTime: shown.startTime24,
      rawEndTime: shown.endTime24,
      startTime: shown.startTime,
      endTime: shown.endTime,
      tz: shown.tz,
      label: shown.label,
      basis: input.speaking ? 'lesson' : 'occurrence',
    };
  }

  // ── The frozen path, KEPT. Reached only with no lesson and no occurrence: a
  // class not materialized yet, every session cancelled, or a weekday that never
  // falls inside the term. The plan's answer for those is today's text.
  const utc =
    frame === 'anchor-tz'
      ? localTimeToUtc(
          slot.dayOfWeek,
          slot.startTime,
          slot.endTime,
          // `|| 'UTC'` rather than a throw, matching what the screens do today:
          // `requests/[id]` and `draft-review` both fall back to UTC for a course
          // with no zone recorded, and part 3 must not turn that into a crash.
          anchorTz || 'UTC',
          fallbackRefDate,
        )
      : { dayOfWeek: slot.dayOfWeek, startTime: slot.startTime, endTime: slot.endTime };

  const raw = utcTimeToLocal(utc.dayOfWeek, utc.startTime, utc.endTime, readerTz, fallbackRefDate);
  const shown = projectLocalTimeRange(
    utc.dayOfWeek,
    utc.startTime,
    utc.endTime,
    readerTz,
    fallbackRefDate,
  );

  return {
    // `projectLocalTimeRange` delegates to `utcTimeToLocal`, so these agree by
    // construction; taken from `raw` so the raw/formatted pair can never
    // disagree about which day it is.
    dayOfWeek: raw.dayOfWeek,
    rawStartTime: raw.startTime,
    rawEndTime: raw.endTime,
    startTime: shown.startTime,
    endTime: shown.endTime,
    tz: shown.tz,
    label: shown.label,
    basis: 'frozen',
  };
}

/**
 * The occurrence a line with no class behind it speaks for, or `null`.
 *
 * ── Which clock the slot's wall-clock values are read in
 *
 * `'anchor-tz'` slots (`catalog_requests.acceptable_patterns`, and the offerings
 * the catalog generates) are stored as the COURSE's wall-clock, and this reads
 * them in the PROGRAMME's clock instead — `window.clock`, never `anchorTz`. That
 * is the decision report #200 recorded and this plan's approval repeats: a course
 * reaches a programme through a many-to-many join and so cannot name one clock,
 * and three courses in production name the other programme's. The two agree for
 * every other course, so this changes the composed hour only where the course
 * setting is the one #200 is correcting.
 *
 * `'utc'` slots (the `availability_slots` TABLE) are already UTC, so their
 * occurrence is composed in UTC and only the reader's leg is left to sample.
 *
 * ── Why the window's one-day fuzz does not matter
 *
 * `windowStart`/`windowEnd` are the term's run as calendar days in `window.clock`,
 * and for a `'utc'` slot they are walked as UTC days. That can include or drop one
 * occurrence at each END of the term. It cannot change the answer that is read:
 * what the reader needs from the occurrence is a date inside the right DST period,
 * and every occurrence of one weekly slot inside a term's final day of slack is in
 * the same one.
 */
function occurrenceFor(input: RepeatingTimeInput): SpeakingLesson | null {
  const { slot, frame, window } = input;
  if (!window) return null;

  return pickSpeakingOccurrence({
    dayOfWeek: slot.dayOfWeek,
    startTime: slot.startTime,
    endTime: slot.endTime,
    clock: frame === 'utc' ? 'UTC' : window.clock,
    windowStart: window.windowStart,
    windowEnd: window.windowEnd,
    now: window.now,
  });
}

/**
 * The same thing with the weekday in front — `"Friday 7 PM–7:55 PM ET"`.
 *
 * Replaced `viewerSlotLabel` at its call sites in part 3, and since part 4 says
 * the real lesson's weekday — which, for an instant that crosses midnight in the
 * reader's zone, is not the weekday the stored triple names. That is the point:
 * `dayOfWeek` comes off the projected instant, so the day and the hour can never
 * contradict each other. `dayNames` is the caller's, because the parent-facing screens
 * pass translated names from next-intl and the admin analytics screens pass
 * their own short English ones.
 */
export function repeatingTimeWithDay(
  input: RepeatingTimeInput,
  dayNames: readonly string[],
): string {
  const time = repeatingTime(input);
  return `${dayNames[time.dayOfWeek]} ${time.label}`;
}
